// Identifies the signed-in user to Amplitude. Events are declared with `defineEvent`
// in each area's `analytics.ts` and sent with `track` from `@visioni/a3s-analytics`.
import { identify, type UserProperties } from "@visioni/a3s-analytics";
import type { User } from "@src/features/auth/types";
import { withCachedUser } from "@src/features/auth/user-profile-merge";
import { MAX_ITEMS_FOR_EXPERIMENT_HEADER } from "@src/constants/experiments";

/** The Amplitude user id for a signed-in user. Both identifies below use it, so they cannot disagree. */
const analyticsUserId = (user: User): string => withCachedUser(user).id;

/**
 * Identify the signed-in user by id only, so a login event tracked next is sent
 * under the user. User properties are left alone: `TenantProvider` sets them with
 * `identifyCurrentUserWithAmplitude` once tenants load, which is after login.
 */
export const identifySignedInUser = (user: User): void => {
  identify({ userId: analyticsUserId(user), properties: {} });
};

interface TenantContext {
  selectedTenant?: { id: string; realm: { fqdn: string } };
  tenants?: readonly { id: string; realm: { fqdn: string } }[];
}

/**
 * Identify current user with tenant context for feature flag targeting.
 * Sends realm, tenantId, and available tenants to Amplitude.
 *
 * @param user - The authenticated user
 * @param tenantContext - Optional tenant context. If not provided, only user info is sent.
 */
export const identifyCurrentUserWithAmplitude = (user: User, tenantContext?: TenantContext): void => {
  const profile = withCachedUser(user);
  const tenants = tenantContext?.tenants ?? [];
  const tenantIds = tenants.map((t) => t.id);
  const realms = [...new Set(tenants.map((t) => t.realm.fqdn))];
  const selectedTenant = tenantContext?.selectedTenant;

  const properties: UserProperties = {
    ...(profile.email && { email: profile.email }),
    ...(profile.name && { name: profile.name }),
    // Every session signs in with Auth0 now. Still set, so a "legacy" value from an earlier session is overwritten.
    auth_type: "auth0",
    ...(tenantContext && {
      ...(selectedTenant && { realm: selectedTenant.realm.fqdn, tenantId: selectedTenant.id }),
      ...(tenantIds.length > 0 && tenantIds.length <= MAX_ITEMS_FOR_EXPERIMENT_HEADER && { tenants: tenantIds }),
      tenantCount: tenantIds.length,
      ...(realms.length > 0 && realms.length <= MAX_ITEMS_FOR_EXPERIMENT_HEADER && { realms }),
    }),
  };
  identify({ userId: analyticsUserId(user), properties });
};
